/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

import { Breadcrumbs, BreadcrumbsBar } from 'uikit/layouts';
import Button from 'uikit/buttons/components/button';
import { Menu, MenuItem } from 'uikit/menus';
import SwitchInput from 'uikit/inputs/components/switchInput';
import Typo from 'uikit/content/components/typo';
import ProcessStatusBar from 'uikit/loaders/components/processStatusBar';

import styles from '../styles/uikitManageManagePageHeader.module.scss';

const ManagePageHeader = ({
  breadcrumbs = [],
  hasHeadingAccessory = false,
  hasKebabMenu = false,
  hasLabel = false,
  hasLiveUpdate = false,
  hasProcessStatusBar = false,
  hasRelatedActions = false,
  hasViewFilter = false,
  headingAccessoryClassName = '',
  headingAccessoryComponent = null,
  isKebabMenuDisabled = false,
  kebabMenuItems = [],
  labelComponent = null,
  liveUpdateProps = {},
  processStatusBarProps = {},
  relatedActionsComponent = null,
  title = '',
  viewFilterProps = {},
}) => {
  const {
    isChecked: isLiveUpdateChecked = false,
    isDisabled: isLiveUpdateDisabled = false,
    onChange: onLiveUpdateChange = () => {},
    toggleId: liveUpdateId = 'live-update-toggle',
  } = liveUpdateProps;

  return (
    <div
      className={classnames(styles.element, { [styles.hasBreadcrumbs]: breadcrumbs.length > 0 })}
    >
      {(breadcrumbs.length > 0 || hasKebabMenu) && (
        <BreadcrumbsBar
          hasMenu={hasKebabMenu}
          isKebabMenuDisabled={isKebabMenuDisabled}
          menuItems={kebabMenuItems}
        >
          {breadcrumbs.length > 0 && <Breadcrumbs breadcrumbs={breadcrumbs} />}
        </BreadcrumbsBar>
      )}
      <header className={classnames(styles.header, { [styles.hasLabel]: hasLabel })}>
        <div className={styles.headerContent}>
          {hasLabel && <div className={styles.headerLabel}>{labelComponent}</div>}
          <div className={styles.headerMain}>
            <div className={styles.headingBlock}>
              <Typo element="h1" variant="d04">
                {title}
              </Typo>
              {hasViewFilter && <ViewFilter {...viewFilterProps} />}
              {hasHeadingAccessory && (
                <div className={classnames(styles.headingAccessory, headingAccessoryClassName)}>
                  {headingAccessoryComponent}
                </div>
              )}
            </div>
            {(hasProcessStatusBar || hasLiveUpdate || hasRelatedActions) && (
              <div className={styles.headerStatusActions}>
                {hasRelatedActions && (
                  <div className={styles.relatedActions}>{relatedActionsComponent}</div>
                )}
                {hasProcessStatusBar && (
                  <div className={styles.processStatusBar}>
                    <ProcessStatusBar {...processStatusBarProps} />
                  </div>
                )}
                {hasLiveUpdate && (
                  <div className={styles.liveUpdate}>
                    <label htmlFor={liveUpdateId}>
                      <Typo color="primary" element="span" variant="s03" weight="medium">
                        {i18n.t('live-update', 'Live Update')}
                      </Typo>
                    </label>
                    <SwitchInput
                      isChecked={isLiveUpdateChecked}
                      isDisabled={isLiveUpdateDisabled}
                      onChange={onLiveUpdateChange}
                      text={isLiveUpdateChecked ? i18n.t('on', 'On') : i18n.t('off', 'Off')}
                      toggleId={liveUpdateId}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
    </div>
  );
};

const ViewFilter = ({ isDisabled = false, onChange = () => {}, options = [], value = '' }) => {
  const textForOptions = Object.fromEntries(options.map((item) => [item.value, item.text]));
  const handleItemClick = (event) => {
    const optionValue = event.target.closest('[data-value]').dataset.value;
    if (optionValue !== value) {
      onChange(optionValue);
    }
  };

  return (
    <Menu
      component={
        <Button
          color="brand-light"
          icon={<KeyboardArrowDownOutlinedIcon />}
          iconPosition="right"
          isDisabled={isDisabled}
          size="small"
          testId="view-filter-button"
          text={textForOptions[value] || ''}
        />
      }
      horizontalPosition="left"
      variant="unbounded"
    >
      {options.map((item) => (
        <MenuItem
          key={item.value}
          isDisabled={item.isDisabled}
          onClick={!item.isDisabled ? handleItemClick : null}
          data-value={item.value}
        >
          <Typo className={styles.text} color="primary" element="span" variant="b01">
            {item.text}
          </Typo>
        </MenuItem>
      ))}
    </Menu>
  );
};

ViewFilter.propTypes = {
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      isDisabled: PropTypes.bool,
      text: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  value: PropTypes.string,
};

ManagePageHeader.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      path: PropTypes.string,
      key: PropTypes.string,
    }),
  ),
  hasBreadcrumbs: PropTypes.bool,
  hasHeadingAccessory: PropTypes.bool,
  hasKebabMenu: PropTypes.bool,
  hasLabel: PropTypes.bool,
  hasLiveUpdate: PropTypes.bool,
  hasProcessStatusBar: PropTypes.bool,
  hasRelatedActions: PropTypes.bool,
  hasViewFilter: PropTypes.bool,
  headingAccessoryClassName: PropTypes.string,
  headingAccessoryComponent: PropTypes.node,
  isKebabMenuDisabled: PropTypes.bool,
  kebabMenuItems: PropTypes.arrayOf(PropTypes.node),
  labelComponent: PropTypes.node,
  liveUpdateProps: PropTypes.shape({
    isChecked: PropTypes.bool,
    isDisabled: PropTypes.bool,
    onChange: PropTypes.func,
    toggleId: PropTypes.string.isRequired,
  }),
  processStatusBarProps: PropTypes.shape({
    ...ProcessStatusBar.propTypes,
  }),
  relatedActionsComponent: PropTypes.node,
  title: PropTypes.string,
  viewFilterProps: PropTypes.shape({
    ...ViewFilter.propTypes,
  }),
};

ManagePageHeader.ViewFilter = ViewFilter;

export default ManagePageHeader;
